/**
 * haptics — thin wrapper over the Vibration API for tactile feedback.
 *
 * The app is touch-heavy but had zero haptic feedback. Light taps on key
 * moments (bottom-sheet snap, watch-zone saved, emergency alert arriving) make
 * it feel native rather than like a web page in a shell. See uplift plan §5.1.
 *
 * Notes:
 * - Vibration is only supported on Android Chrome/WebView (iOS Safari ignores
 *   it). Calls are safe no-ops elsewhere.
 * - Respects prefers-reduced-motion: users who opt out of motion get no buzz.
 * - Kept intentionally short and sparse; overuse is worse than none.
 */

type HapticPattern = 'light' | 'medium' | 'success' | 'warning';

const PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 8,
  medium: 15,
  success: [10, 40, 10],
  warning: [20, 60, 20],
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function haptic(pattern: HapticPattern = 'light'): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  if (prefersReducedMotion()) return;
  try {
    navigator.vibrate(PATTERNS[pattern]);
  } catch {
    /* vibrate can throw if called too frequently / without a user gesture */
  }
}
